import * as Sentry from '@sentry/react';

function parseSampleRate(raw: string | undefined, fallback: number): number {
  const value = parseFloat(raw ?? '');
  if (!Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

// Scripts we load but don't ship or control. Errors originating in these
// (e.g. Cloudflare Web Analytics' beacon calling Array.prototype.at() on
// browsers older than Safari 15.4) are not our bugs and we can't fix them,
// so we drop them at the SDK rather than triaging the noise in Sentry.
// Matching by frame URL keeps this scoped to third-party code — errors from
// our own bundle are still reported, even when they share a message. Every
// pattern is anchored to the script's third-party host (not just a filename)
// so a first-party URL that happens to contain one of these strings is never
// suppressed.
const THIRD_PARTY_SCRIPT_URLS: RegExp[] = [
  /static\.cloudflareinsights\.com/i, // Cloudflare Web Analytics beacon (beacon.min.js)
  /googletagmanager\.com/i, // GTM + gtag.js
  /google-analytics\.com/i,
  /connect\.facebook\.net/i, // Meta Pixel
];

// Errors thrown by JS that in-app browsers (Instagram, Facebook, etc.) inject
// into every page they render. Meta's in-app browser instruments navigation via
// a native bridge (sendDataToNative -> sendPageHideMessage) that reaches for
// `window.webkit.messageHandlers`; when that object is missing it throws an
// uncaught TypeError. Because the injected script runs in the page's own
// context, Sentry attributes the frame to our URL, so denyUrls can't catch it —
// we filter on the message instead. `messageHandlers` never appears in our own
// bundle, so this only suppresses the injected-bridge noise.
const IGNORED_ERROR_MESSAGES: (string | RegExp)[] = [
  /messageHandlers/i, // Meta/WebKit in-app browser native bridge (window.webkit.messageHandlers)
];

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // v11 collects request data by default; this keeps the restrictive v10 default.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
    },
    denyUrls: THIRD_PARTY_SCRIPT_URLS,
    ignoreErrors: IGNORED_ERROR_MESSAGES,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    replaysSessionSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0,
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
      1.0,
    ),
  });
}
